import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { formatCurrency } from "../utils/format";
import { toast } from "sonner";
import { FileText, RefreshCw, Zap, RotateCcw, Upload, ArrowLeft, ChevronDown } from "lucide-react";

export default function FaturalarDashboard() {
  const navigate = useNavigate();
  const { logout } = useApp();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateFilter, setDateFilter] = useState("buAy");
  const [faturaSayilari, setFaturaSayilari] = useState({
    edm_alis: 0, edm_satis: 0, netsis_alis: 0, netsis_satis: 0,
    edm_iade_gelen: 0, edm_iade_giden: 0, netsis_iade_gelen: 0, netsis_iade_giden: 0,
    edm_alis_tutar: 0, edm_satis_tutar: 0, netsis_alis_tutar: 0, netsis_satis_tutar: 0,
    edm_iade_gelen_tutar: 0, edm_iade_giden_tutar: 0, netsis_iade_gelen_tutar: 0, netsis_iade_giden_tutar: 0
  });

  const getDateRange = () => {
    const today = new Date();
    let baslangic, bitis;
    
    switch(dateFilter) {
      case "bugun":
        baslangic = bitis = today.toISOString().split("T")[0];
        break;
      case "buHafta":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1);
        baslangic = weekStart.toISOString().split("T")[0];
        bitis = today.toISOString().split("T")[0];
        break;
      case "buAy":
        baslangic = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-01";
        bitis = today.toISOString().split("T")[0];
        break;
      case "gecenAy":
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        baslangic = lastMonth.toISOString().split("T")[0];
        bitis = lastMonthEnd.toISOString().split("T")[0];
        break;
      case "buYil":
        baslangic = today.getFullYear() + "-01-01";
        bitis = today.toISOString().split("T")[0];
        break;
      default:
        baslangic = bitis = today.toISOString().split("T")[0];
    }
    return { baslangic, bitis };
  };

  useEffect(() => {
    loadFaturaSayilari();
  }, [dateFilter]);

  const loadFaturaSayilari = async () => {
    try {
      setLoading(true);
      const { baslangic, bitis } = getDateRange();
      const response = await api.getMibaFaturaSayilari(baslangic, bitis);
      if (response.data && response.data.sayilar) {
        setFaturaSayilari(response.data.sayilar);
      }
    } catch (error) {
      console.error("Fatura sayilari yuklenemedi:", error);
      toast.error("Fatura sayilari yuklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { baslangic, bitis } = getDateRange();
      await api.syncFaturalar({ sirket: "miba", kaynak: "netsis", tur: "giden", baslangicTarihi: baslangic, bitisTarihi: bitis });
      await api.syncFaturalar({ sirket: "miba", kaynak: "netsis", tur: "gelen", baslangicTarihi: baslangic, bitisTarihi: bitis });
      toast.success("Senkronizasyon tamamlandi");
      loadFaturaSayilari();
    } catch (error) {
      console.error("Sync hatasi:", error);
      toast.error("Senkronizasyon hatasi");
    } finally {
      setSyncing(false);
    }
  };

  const handleTumSync = async () => {
    setSyncing(true);
    try {
      await api.syncFaturalar({ sirket: "miba", kaynak: "netsis", tur: "giden" });
      await api.syncFaturalar({ sirket: "miba", kaynak: "netsis", tur: "gelen" });
      toast.success("Tum veriler senkronize edildi");
      loadFaturaSayilari();
    } catch (error) {
      console.error("Tum sync hatasi:", error);
      toast.error("Senkronizasyon hatasi");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  };

  const FaturaCard = ({ title, count, amount, color, onClick }) => (
    <div 
      onClick={onClick}
      className={"relative p-6 rounded-2xl cursor-pointer transition-all hover:scale-105 hover:shadow-lg " + color}
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-3 bg-white/20">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-sm font-bold text-white mb-1">{title}</h3>
        <p className="text-2xl font-bold text-white">{formatCurrency(amount || 0)}</p>
        <p className="text-xs text-white/80 mt-1">{count || 0} adet</p>
      </div>
      <ChevronDown className="absolute bottom-2 right-2 w-4 h-4 text-white/50" />
    </div>
  );

  const ActionCard = ({ title, icon: Icon, color, onClick, disabled }) => (
    <div 
      onClick={disabled ? null : onClick}
      className={"p-6 rounded-2xl cursor-pointer transition-all hover:scale-105 hover:shadow-lg " + color + (disabled ? " opacity-50 cursor-not-allowed" : "")}
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-3 bg-white/20">
          <Icon className={"w-8 h-8 text-white " + (disabled ? "animate-spin" : "")} />
        </div>
        <h3 className="text-sm font-bold text-white">{title}</h3>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{background: "linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0ea5e9 100%)"}}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-xl">M</div>
            <div>
              <h1 className="text-2xl font-bold text-white">MIBA<span className="text-emerald-300">GRUP</span></h1>
              <p className="text-xs text-white/70">OTOMOTIV A.S</p>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mt-4">FATURALAR</h2>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          <button onClick={() => navigate("/menu")} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Ana Menu
          </button>
          <div className="px-4 py-2 bg-white/20 rounded-full text-white text-sm">
            {formatDate(new Date())}
          </div>
          {["bugun", "buHafta", "buAy", "gecenAy", "buYil"].map((filter) => (
            <button
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={"px-4 py-2 rounded-full text-sm transition-all " + (dateFilter === filter ? "bg-teal-600 text-white" : "bg-white/20 text-white hover:bg-white/30")}
            >
              {filter === "bugun" ? "Bugun" : filter === "buHafta" ? "Bu Hafta" : filter === "buAy" ? "Bu Ay" : filter === "gecenAy" ? "Gecen Ay" : "Bu Yil"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <FaturaCard title="EDM ALIS" count={faturaSayilari.edm_alis} amount={faturaSayilari.edm_alis_tutar} color="bg-blue-500" onClick={() => navigate("/faturalar?kaynak=edm&tur=gelen")} />
          <FaturaCard title="EDM SATIS" count={faturaSayilari.edm_satis} amount={faturaSayilari.edm_satis_tutar} color="bg-blue-500" onClick={() => navigate("/faturalar?kaynak=edm&tur=giden")} />
          <FaturaCard title="NETSIS ALIS" count={faturaSayilari.netsis_alis} amount={faturaSayilari.netsis_alis_tutar} color="bg-teal-500" onClick={() => navigate("/faturalar?kaynak=netsis&tur=gelen")} />
          <FaturaCard title="NETSIS SATIS" count={faturaSayilari.netsis_satis} amount={faturaSayilari.netsis_satis_tutar} color="bg-teal-500" onClick={() => navigate("/faturalar?kaynak=netsis&tur=giden")} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <FaturaCard title="EDM IADE GELEN" count={faturaSayilari.edm_iade_gelen} amount={faturaSayilari.edm_iade_gelen_tutar} color="bg-orange-500" onClick={() => navigate("/faturalar?kaynak=edm&tur=iade_gelen")} />
          <FaturaCard title="EDM IADE GIDEN" count={faturaSayilari.edm_iade_giden} amount={faturaSayilari.edm_iade_giden_tutar} color="bg-orange-500" onClick={() => navigate("/faturalar?kaynak=edm&tur=iade_giden")} />
          <FaturaCard title="NETSIS IADE GELEN" count={faturaSayilari.netsis_iade_gelen} amount={faturaSayilari.netsis_iade_gelen_tutar} color="bg-red-400" onClick={() => navigate("/faturalar?kaynak=netsis&tur=iade_gelen")} />
          <FaturaCard title="NETSIS IADE GIDEN" count={faturaSayilari.netsis_iade_giden} amount={faturaSayilari.netsis_iade_giden_tutar} color="bg-red-400" onClick={() => navigate("/faturalar?kaynak=netsis&tur=iade_giden")} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <ActionCard title="EDM NETSIS KARSILASTIR" icon={FileText} color="bg-emerald-600" onClick={() => toast.info("EDM-Netsis karsilastirma")} />
          <ActionCard title="SYNC" icon={RefreshCw} color="bg-cyan-500" onClick={handleSync} disabled={syncing} />
          <ActionCard title="OTOMATIK" icon={Zap} color="bg-green-500" onClick={() => toast.info("Otomatik senkronizasyon aktif")} />
          <ActionCard title="TUM SYNC" icon={RotateCcw} color="bg-teal-600" onClick={handleTumSync} disabled={syncing} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <ActionCard title="NETSIS VERI AKTAR" icon={Upload} color="bg-slate-500" onClick={handleTumSync} disabled={syncing} />
        </div>

        <div className="flex justify-center gap-4">
          <button onClick={() => navigate("/menu")} className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium">Sube Degistir</button>
          <button onClick={() => { logout(); navigate("/"); }} className="px-6 py-3 bg-red-500 hover:bg-red-600 rounded-lg text-white font-medium">Cikis Yap</button>
        </div>

        <div className="text-center mt-8 text-white/50 text-xs">v0.1.8</div>
      </div>
    </div>
  );
}
